
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Zap, Shield, Clock, Star, CheckCircle, Users, ImageIcon, Download, Globe, Smartphone } from 'lucide-react';

interface SEOContentProps {
  showOnlyFirstSections?: boolean;
}

const SEOContent = ({ showOnlyFirstSections = false }: SEOContentProps) => {
  const benefits = [
    {
      icon: Zap,
      title: "Compresión Ultrarrápida",
      description: "Optimiza tus imágenes JPG, PNG, WebP y GIF en segundos con nuestro algoritmo de compresión avanzado"
    },
    {
      icon: Shield,
      title: "100% Seguro y Privado",
      description: "Tus imágenes se procesan localmente en tu navegador, nunca se suben a servidores externos"
    },
    {
      icon: Clock,
      title: "Disponible 24/7",
      description: "Accede a nuestro compresor de imágenes online en cualquier momento desde cualquier dispositivo"
    },
    {
      icon: Star,
      title: "Calidad Premium",
      description: "Mantén la calidad visual óptima mientras reduces hasta 90% el tamaño de archivo"
    }
  ];

  const features = [
    "Comprimir imágenes JPEG, JPG, PNG sin pérdida de calidad visible",
    "Reducir tamaño de archivo hasta 90% del tamaño original",
    "Optimización automática para web, móvil y redes sociales",
    "Soporte para múltiples formatos: JPG, JPEG, PNG, WebP, GIF, AVIF",
    "Procesamiento por lotes para múltiples imágenes simultáneamente",
    "Vista previa comparativa antes y después de la compresión",
    "Descarga instantánea de imágenes optimizadas en ZIP"
  ];

  const advancedFeatures = [
    {
      icon: ImageIcon,
      title: "Algoritmos Inteligentes",
      description: "Utilizamos algoritmos de compresión adaptativos que analizan cada imagen para aplicar la optimización más efectiva"
    },
    {
      icon: Download,
      title: "Descarga Masiva",
      description: "Descarga todas tus imágenes comprimidas de una vez en un archivo ZIP organizado"
    },
    {
      icon: Globe,
      title: "Optimización Web",
      description: "Imágenes perfectamente optimizadas para mejorar el SEO y la velocidad de carga de tu sitio web"
    },
    {
      icon: Smartphone,
      title: "Compatible Móvil",
      description: "Funciona perfectamente en dispositivos móviles, tablets y computadoras de escritorio"
    }
  ];

  if (showOnlyFirstSections) {
    return (
      <div className="space-y-12">
        {/* Benefits Grid */}
        <section>
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
            Ventajas de comprimir tus imágenes online
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Descubre por qué más de 50,000 usuarios eligen nuestro <strong>compresor de imágenes</strong> para 
            <strong> optimizar sus archivos JPG, PNG y WebP</strong> de forma gratuita y segura.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all duration-300 border-2 hover:border-blue-200">
                <CardHeader>
                  <benefit.icon className="h-12 w-12 text-blue-600 mx-auto mb-2" />
                  <CardTitle className="text-lg">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm">{benefit.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Advanced Features */}
        <section>
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
            Tecnología Avanzada de Compresión
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Nuestra herramienta utiliza los últimos algoritmos de <strong>compresión de imágenes</strong> para 
            garantizar los mejores resultados en <strong>optimización de fotografías</strong> y gráficos.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advancedFeatures.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <feature.icon className="h-10 w-10 text-purple-600 mx-auto mb-2" />
                  <CardTitle className="text-base">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm">{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* Features List */}
      <section>
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
          Características principales del compresor
        </h2>
        <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
          Todas las funciones que necesitas para <strong>comprimir y optimizar imágenes</strong> de forma 
          profesional, completamente gratis y sin instalación.
        </p>
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
          ¿Por qué elegir nuestro compresor de imágenes?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-red-600 mb-4">❌ Otros compresores</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Requieren instalación de software</li>
              <li>• Procesan imágenes en servidores externos</li>
              <li>• Limitan formatos y tamaños</li>
              <li>• Interfaz compleja y confusa</li>
              <li>• Calidad de compresión inferior</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border-2 border-green-200">
            <h3 className="text-xl font-semibold text-green-600 mb-4">✅ Nuestro compresor</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• 100% online, sin instalación</li>
              <li>• Procesamiento local y seguro</li>
              <li>• Múltiples formatos soportados</li>
              <li>• Interfaz intuitiva y moderna</li>
              <li>• Calidad premium garantizada</li>
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ SEO */}
      <section>
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
          Preguntas frecuentes sobre compresión de imágenes
        </h2>
        <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
          Resolvemos las dudas más comunes sobre cómo <strong>comprimir imágenes</strong> y 
          <strong> optimizar archivos</strong> para web de forma efectiva.
        </p>
        <div className="max-w-4xl mx-auto space-y-6">
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg">¿Qué formatos de imagen puedo comprimir?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Nuestro <strong>compresor de imágenes</strong> soporta los formatos más populares: <strong>JPEG/JPG, PNG, WebP y GIF</strong>. 
                Cada formato se optimiza con algoritmos específicos para obtener la máxima compresión manteniendo la calidad visual. 
                Próximamente agregaremos soporte para AVIF y otros formatos modernos.
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg">¿Cuál es la diferencia entre comprimir JPG y PNG?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                <strong>Comprimir JPG</strong> es ideal para fotografías ya que usa compresión con pérdida controlada que reduce significativamente el tamaño. 
                <strong>Comprimir PNG</strong> es mejor para imágenes con transparencia, logos o gráficos, usando compresión sin pérdida que preserva todos los detalles.
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg">¿Por qué comprimir imágenes para web es importante?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                <strong>Comprimir imágenes</strong> para web mejora significativamente la velocidad de carga de tu sitio web, 
                reduce el consumo de ancho de banda y mejora la experiencia del usuario. Google considera la velocidad de carga 
                como factor crucial de ranking SEO, por lo que <strong>optimizar imágenes</strong> puede mejorar tu posicionamiento.
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg">¿Cuánto puedo reducir el tamaño de mis imágenes?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Dependiendo del formato y contenido original, puedes <strong>reducir el tamaño hasta 90%</strong> sin pérdida notable de calidad. 
                Las imágenes JPG suelen comprimirse más que PNG, mientras que WebP ofrece la mejor relación 
                calidad-tamaño para uso web moderno. Nuestro algoritmo automáticamente encuentra el balance óptimo.
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg">¿Es seguro usar un compresor de imágenes online?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Absolutamente. Nuestro <strong>compresor procesa todas las imágenes localmente</strong> en tu navegador, 
                lo que significa que tus archivos nunca se envían a nuestros servidores. Esto garantiza total privacidad y seguridad. 
                Además, puedes usar la herramienta sin registro ni instalación de software.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust indicators */}
      <section className="text-center py-12 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          Confianza y Calidad Garantizada
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="flex flex-col items-center space-y-2">
            <Users className="h-8 w-8 text-blue-600" />
            <span className="font-semibold text-gray-800">50,000+ Usuarios</span>
            <span className="text-sm text-gray-600">Confían en nuestra herramienta</span>
          </div>
          <div className="flex flex-col items-center space-y-2">
            <Shield className="h-8 w-8 text-green-600" />
            <span className="font-semibold text-gray-800">100% Seguro</span>
            <span className="text-sm text-gray-600">Procesamiento local privado</span>
          </div>
          <div className="flex flex-col items-center space-y-2">
            <Zap className="h-8 w-8 text-purple-600" />
            <span className="font-semibold text-gray-800">Compresión Instantánea</span>
            <span className="text-sm text-gray-600">Resultados en segundos</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SEOContent;
